import { useMemo } from "react";
import type { ReportFilters, ReportOptions } from "@/components/feature/reports/types";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { useDepenses } from "@/pages/proprietaire/depenses/DepensesProvider";
import { useDocuments } from "@/pages/proprietaire/documents/DocumentsProvider";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLoyers } from "@/pages/proprietaire/loyers/LoyersProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import { useMaintenance } from "@/pages/proprietaire/maintenance/MaintenanceProvider";
import { usePaiements } from "@/pages/proprietaire/paiements/PaiementsProvider";
import { buildOwnerReport, getOwnerStatutConfig } from "./buildReport";
import { OWNER_REPORT_TYPES } from "./catalog";

/**
 * Assemble le moteur de rapports de l'espace Propriétaire à partir des stores
 * de l'espace. Le rapport ne s'appuie que sur les données accessibles au compte
 * connecté. Le jour où la base de données est branchée, seuls les stores
 * changent : ce hook et le moteur de rapports restent inchangés.
 */
export function useOwnerReports() {
  const { immeubles } = useImmeubles();
  const { biens } = useBiens();
  const { locataires } = useLocataires();
  const { contrats } = useContrats();
  const { loyers } = useLoyers();
  const { paiements } = usePaiements();
  const { depenses } = useDepenses();
  const { interventions } = useMaintenance();
  const { documents } = useDocuments();

  const options: ReportOptions = useMemo(
    () => ({
      immeubles: immeubles.map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
      biens: biens.map((bien) => ({
        value: bien.id,
        label: `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}`,
        immeubleId: bien.immeubleId,
      })),
      locataires: locataires.map((locataire) => ({ value: locataire.id, label: locataireNomComplet(locataire) })),
    }),
    [immeubles, biens, locataires]
  );

  const buildReport = (typeId: string, filters: ReportFilters) =>
    buildOwnerReport(typeId, filters, {
      immeubles,
      biens,
      locataires,
      contrats,
      loyers,
      paiements,
      depenses,
      interventions,
      documents,
    });

  return {
    reportTypes: OWNER_REPORT_TYPES,
    options,
    getStatutConfig: getOwnerStatutConfig,
    buildReport,
  };
}